import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@/app/config/env';

import { ApiError, normalizeApiError } from './api-error';
import { unwrap, type GeneralResponse } from './backend-envelopes';

/**
 * The one HTTP client.
 *
 * No component, page or hook may import axios directly. The call chain is
 * always: page → *.queries.ts → *.api.ts → api-client (§11). That indirection
 * is what makes it possible to change auth, retries or base URL in one place.
 */

/**
 * Auth is injected rather than imported.
 *
 * The client needs a token; the auth feature needs the client to fetch one.
 * Importing either way round creates a cycle, so the auth feature registers
 * itself at startup via `configureApiClient`. It also means the client has no
 * opinion about where tokens are stored, which is what lets the backend move
 * to HttpOnly cookies later without touching this file (§16).
 */
interface ApiClientAuth {
  getAccessToken: () => string | null;
  /** Resolves to a fresh access token, or null when refresh is impossible. */
  refreshTokens: () => Promise<string | null>;
  /** Called when the session is definitively over. Should redirect to login. */
  onAuthFailure: () => void;
}

let auth: ApiClientAuth | null = null;

export function configureApiClient(next: ApiClientAuth) {
  auth = next;
}

/** Per-request escape hatches. */
export interface RequestOptions extends AxiosRequestConfig {
  /**
   * Skip the 401 → refresh → retry dance. Set on the login and refresh calls
   * themselves, which would otherwise recurse.
   */
  skipAuthRefresh?: boolean;
}

type RetryableConfig = InternalAxiosRequestConfig & {
  skipAuthRefresh?: boolean;
  _retried?: boolean;
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = auth?.getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * Single-flight refresh.
 *
 * If three requests 401 at once we must not fire three refreshes — the backend
 * stores exactly one refresh token per user and rotates it on use
 * (UT system/Services/UsersService.cs:899), so concurrent refreshes would
 * invalidate each other and log the user out. Instead the first failure starts
 * a refresh and the rest await the same promise.
 */
let refreshInFlight: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (auth?.refreshTokens() ?? Promise.resolve(null)).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response || !error.config) {
      return Promise.reject(normalizeApiError(error));
    }

    // A download (responseType arraybuffer) still gets its error envelope as JSON.
    const { data } = error.response;
    if (data instanceof ArrayBuffer) {
      try {
        error.response.data = JSON.parse(new TextDecoder().decode(data)) as unknown;
      } catch {
        // Not JSON; the message falls back to the generic one.
      }
    }

    const config = error.config as RetryableConfig;
    const isAuthError = error.response.status === 401;
    const canRetry = isAuthError && !config.skipAuthRefresh && !config._retried && auth !== null;

    if (!canRetry) {
      if (isAuthError && !config.skipAuthRefresh) {
        auth?.onAuthFailure();
      }
      return Promise.reject(normalizeApiError(error));
    }

    config._retried = true;

    try {
      const token = await refreshOnce();
      if (!token) {
        auth?.onAuthFailure();
        return Promise.reject(normalizeApiError(error));
      }
      config.headers.set('Authorization', `Bearer ${token}`);
      return await apiClient.request(config);
    } catch {
      auth?.onAuthFailure();
      return Promise.reject(normalizeApiError(error));
    }
  },
);

/**
 * Typed helpers that unwrap the backend envelope.
 *
 * Feature API modules use these rather than `apiClient` directly, so that
 * `GeneralResponse<T>` unwrapping happens once instead of in every function.
 */
export const http = {
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    const response = await apiClient.get<GeneralResponse<T> | T>(url, options);
    return unwrap<T>(response.data);
  },

  async post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await apiClient.post<GeneralResponse<T> | T>(url, body, options);
    return unwrap<T>(response.data);
  },

  async put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await apiClient.put<GeneralResponse<T> | T>(url, body, options);
    return unwrap<T>(response.data);
  },

  async patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await apiClient.patch<GeneralResponse<T> | T>(url, body, options);
    return unwrap<T>(response.data);
  },

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    const response = await apiClient.delete<GeneralResponse<T> | T>(url, options);
    return unwrap<T>(response.data);
  },

  /**
   * A multipart upload (a file plus form fields), e.g. a catalogue import sheet.
   *
   * The instance default `Content-Type: application/json` would make axios
   * serialise the FormData as JSON, so it is removed (`false`) and the
   * runtime writes `multipart/form-data` with its boundary. Uploads get a
   * longer timeout than the 20 s default: the server validates every row
   * before it answers. (Tests: see `src/test/node-form-data.ts`.)
   */
  async upload<T>(url: string, form: FormData, options?: RequestOptions): Promise<T> {
    const response = await apiClient.post<GeneralResponse<T> | T>(url, form, {
      timeout: 120_000,
      ...options,
      headers: { ...options?.headers, 'Content-Type': false },
    });
    return unwrap<T>(response.data);
  },

  /** A file response (CSV export). The file name comes from Content-Disposition when present. */
  async download(
    url: string,
    options?: RequestOptions,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    // arraybuffer rather than blob: it works in every runtime, including jsdom.
    const response = await apiClient.get<ArrayBuffer>(url, {
      ...options,
      responseType: 'arraybuffer',
    });
    const type = String(response.headers['content-type'] ?? 'application/octet-stream');
    const disposition = String(response.headers['content-disposition'] ?? '');
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return {
      blob: new Blob([response.data], { type }),
      fileName: match?.[1] ? decodeURIComponent(match[1]) : null,
    };
  },
};

export { ApiError };
